<template>
  <Dialog :title="dialogTitle" v-model="dialogVisible" width="1280">
    <el-form
      ref="formRef"
      :model="formData"
      :rules="formRules"
      label-width="120px"
      v-loading="formLoading"
    >
      <el-row>
        <el-col :span="8">
          <el-form-item label="商机名称" prop="name">
            <el-input v-model="formData.name" placeholder="请输入商机名称" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="负责人" prop="ownerUserId">
            <el-select
              v-model="formData.ownerUserId"
              :disabled="formType !== 'create'"
              class="w-1/1"
            >
              <el-option
                v-for="item in userOptions"
                :key="item.id"
                :label="item.nickname"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="客户名称" prop="customerId">
            <el-select
              :disabled="formData.customerDefault"
              v-model="formData.customerId"
              placeholder="请选择客户"
              class="w-1/1"
            >
              <el-option
                v-for="item in customerList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="8">
          <el-form-item label="商机状态组" prop="statusTypeId">
            <el-select
              v-model="formData.statusTypeId"
              placeholder="请选择商机状态组"
              clearable
              class="w-1/1"
              :disabled="formType !== 'create'"
            >
              <el-option
                v-for="item in statusTypeList"
                :key="item.id!"
                :label="item.name"
                :value="item.id!"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="预计成交日期" prop="dealTime">
            <el-date-picker
              v-model="formData.dealTime"
              type="date"
              value-format="x"
              placeholder="选择预计成交日期"
              class="!w-1/1"
            />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="备注" prop="remark">
            <el-input type="textarea" v-model="formData.remark" placeholder="请输入备注" />
          </el-form-item>
        </el-col>
      </el-row>
      <!-- 子表的表单 -->
      <ContentWrap>
        <el-tabs v-model="subTabsName" class="-mt-15px -mb-10px">
          <el-tab-pane label="产品清单" name="product">
            <BusinessProductForm
              ref="productFormRef"
              :products="formData.products"
              :disabled="disabled"
            />
          </el-tab-pane>
        </el-tabs>
      </ContentWrap>
      <el-row>
        <el-col :span="8">
          <el-form-item label="产品总金额" prop="totalProductPrice">
            <el-input
              disabled
              v-model="formData.totalProductPrice"
              :formatter="erpPriceInputFormatter"
            />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="整单折扣（%）" prop="discountPercent">
            <el-input-number
              v-model="formData.discountPercent"
              placeholder="请输入整单折扣"
              controls-position="right"
              :min="0"
              :precision="2"
              :disabled="hasProducts"
              class="!w-1/1"
            />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="商机金额" prop="totalPrice">
            <!-- 无产品明细的商机（原型「客户 - 商机」里很常见）必须能直接填金额；有明细时由产品清单自动算 -->
            <el-input-number
              v-model="formData.totalPrice"
              :placeholder="hasProducts ? '' : '无产品明细时，可直接填写商机金额'"
              controls-position="right"
              :min="0"
              :precision="2"
              :disabled="hasProducts"
              class="!w-1/1"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="相关文件" prop="fileUrls">
            <!-- 商机附件是合同、报价等业务凭据，不属于运营素材，一律直接上传，不走素材库 -->
            <UploadFile
              v-model="formData.fileUrls"
              class="min-w-80px"
              :use-material-library="false"
            />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <template #footer>
      <el-button @click="submitForm" type="primary" :disabled="formLoading">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>
<script setup lang="ts">
import * as BusinessApi from '@/api/crm/business'
import * as BusinessStatusApi from '@/api/crm/business/status'
import * as CustomerApi from '@/api/crm/customer'
import * as UserApi from '@/api/system/user'
import { getCurrentUserId } from '@/utils/auth'
import { notPureNumber } from '@/utils/formRules'
import BusinessProductForm from './components/BusinessProductForm.vue'
import { erpPriceMultiply, erpPriceInputFormatter } from '@/utils'

type BusinessProduct = NonNullable<BusinessApi.BusinessVO['products']>[number]

const { t } = useI18n() // 国际化
const message = useMessage() // 消息弹窗

const dialogVisible = ref(false) // 弹窗的是否展示
const dialogTitle = ref('') // 弹窗的标题
const formLoading = ref(false) // 表单的加载中：1）修改时的数据加载；2）提交的按钮禁用
const formType = ref('') // 表单的类型：create - 新增；update - 修改
const disabled = computed(() => formType.value === 'detail')
const formData = ref({
  id: undefined as number | undefined,
  name: undefined as string | undefined,
  customerId: undefined as number | undefined,
  ownerUserId: undefined as number | undefined,
  statusTypeId: undefined as number | undefined,
  dealTime: undefined,
  discountPercent: 0,
  totalProductPrice: undefined as number | undefined,
  totalPrice: undefined as number | undefined,
  remark: undefined as string | undefined,
  fileUrls: [] as string[],
  products: [] as BusinessProduct[],
  contactId: undefined as number | undefined,
  customerDefault: false
})

/** 是否存在产品明细：有明细时「产品总金额 / 整单折扣 / 商机金额」自动算；无明细时商机金额可直接填写 */
const hasProducts = computed(() => (formData.value.products?.length ?? 0) > 0)

/**
 * 商机金额校验
 *
 * - 有产品明细：金额由产品清单自动计算，用户改不了，不校验
 * - 无产品明细：**允许留空**（后端按 0 处理），填了则不得小于 0
 *
 * ⚠️ 刻意不加 `required`：原型里「*金额」带必填标记，但现有系统从
 *    客户详情页 / 联系人详情页「新建商机」时本就没有产品、也从不填金额，
 *    一加必填会把这些既有流程直接拦死。要强制必填时在这里补一个空值分支即可。
 */
const validateTotalPrice = (_rule: any, value: any, callback: any) => {
  if (formData.value.products?.length) {
    callback()
    return
  }
  if (value === undefined || value === null || value === '') {
    callback()
    return
  }
  if (Number(value) < 0) {
    callback(new Error('商机金额不能小于 0'))
    return
  }
  callback()
}

const formRules = reactive({
  name: [
    { required: true, message: '商机名称不能为空', trigger: 'blur' },
    notPureNumber('商机名称')
  ],
  customerId: [{ required: true, message: '客户不能为空', trigger: 'blur' }],
  ownerUserId: [{ required: true, message: '负责人不能为空', trigger: 'blur' }],
  statusTypeId: [{ required: true, message: '商机状态组不能为空', trigger: 'blur' }],
  totalPrice: [{ validator: validateTotalPrice, trigger: ['blur', 'change'] }]
})
const formRef = ref() // 表单 Ref
const userOptions = ref<UserApi.UserVO[]>([]) // 用户列表
const statusTypeList = ref<BusinessStatusApi.BusinessStatusTypeVO[]>([]) // 商机状态类型列表
const customerList = ref<CustomerApi.CustomerVO[]>([]) // 客户列表的数据

/** 子表的表单 */
const subTabsName = ref('product')
const productFormRef = ref()

/** 计算 discountPrice、totalPrice 价格 */
watch(
  () => formData.value,
  (val) => {
    if (!val) {
      return
    }
    const products = val.products ?? []
    // 无产品明细：产品总金额恒为 0，商机金额由用户直接填写 ⇒ 不覆盖 totalPrice
    if (products.length === 0) {
      formData.value.totalProductPrice = 0
      return
    }
    const totalProductPrice = products.reduce((prev, curr) => prev + (curr.totalPrice ?? 0), 0)
    const discountPrice =
      val.discountPercent != null
        ? (erpPriceMultiply(totalProductPrice, (val.discountPercent ?? 0) / 100.0) ?? 0)
        : 0
    // 赋值
    formData.value.totalProductPrice = totalProductPrice
    formData.value.totalPrice = totalProductPrice - discountPrice
  },
  { deep: true }
)

/** 打开弹窗 */
const open = async (type: string, id?: number, customerId?: number, contactId?: number) => {
  dialogVisible.value = true
  dialogTitle.value = t('action.' + type)
  formType.value = type
  resetForm()
  // 修改时，设置数据
  if (id) {
    formLoading.value = true
    try {
      formData.value = await BusinessApi.getBusiness(id)
    } finally {
      formLoading.value = false
    }
  } else {
    if (customerId) {
      formData.value.customerId = customerId
      formData.value.customerDefault = true // 默认客户的选择，不允许变
    }
    // 自动关联 contactId 联系人编号
    if (contactId) {
      formData.value.contactId = contactId
    }
  }
  // 获得客户列表
  customerList.value = await CustomerApi.getCustomerSimpleList()
  // 加载商机状态类型列表
  statusTypeList.value = await BusinessStatusApi.getBusinessStatusTypeSimpleList()
  // 获得用户列表
  userOptions.value = await UserApi.getSimpleUserList()
  // 默认新建时选中自己
  if (formType.value === 'create') {
    formData.value.ownerUserId = getCurrentUserId()
  }
}
defineExpose({ open }) // 提供 open 方法，用于打开弹窗

/** 提交表单 */
const emit = defineEmits(['success']) // 定义 success 事件，用于操作成功后的回调
const submitForm = async () => {
  // 校验表单
  if (!formRef) return
  const valid = await formRef.value.validate()
  if (!valid) return
  await productFormRef.value.validate()
  // 提交请求
  formLoading.value = true
  try {
    const data = formData.value as unknown as BusinessApi.BusinessVO
    if (formType.value === 'create') {
      await BusinessApi.createBusiness(data)
      message.success(t('common.createSuccess'))
    } else {
      await BusinessApi.updateBusiness(data)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    // 发送操作成功的事件
    emit('success')
  } finally {
    formLoading.value = false
  }
}

/** 重置表单 */
const resetForm = () => {
  formData.value = {
    id: undefined,
    name: undefined,
    customerId: undefined,
    ownerUserId: undefined,
    statusTypeId: undefined,
    dealTime: undefined,
    discountPercent: 0,
    totalProductPrice: undefined,
    totalPrice: undefined,
    remark: undefined,
    fileUrls: [],
    products: [],
    contactId: undefined,
    customerDefault: false
  }
  formRef.value?.resetFields()
}
</script>
